import { useCallback, useId, useRef, type ChangeEvent, type ReactNode } from 'react';
import { Label } from '../Label';
import { SparkIcon } from '../SparkIcon';
import './AvatarUpload.css';

export type AvatarUploadEmptyVariant = 'dashed' | 'initials';

/** 72px circular image picker with a label, used for workspace logo / user image (Figma onboarding 9237:3197 / 9237:3233). */
export type AvatarUploadProps = {
  /** Field label rendered above the circle (Spark `Label`). */
  label?: ReactNode;
  /**
   * How the circle looks before an image is chosen:
   * `'dashed'` — dashed `--primitive-blue-500` ring with a 24px `plus` icon;
   * `'initials'` — `--neon-peach-500` disc showing the first letter of `initials`.
   */
  emptyVariant?: AvatarUploadEmptyVariant;
  /** Source for the initial letter in the `'initials'` empty variant (first character is shown, upper-cased). */
  initials?: string;
  /** Chosen image (data URL or http URL). When set, the image fills the circle. */
  src?: string | null;
  /** `alt` for the chosen image. */
  alt?: string;
  /** `accept` attribute for the hidden file input. */
  accept?: string;
  /** Called with the first chosen file. The input is reset afterwards so the same file can be re-picked. */
  onFileSelected?: (file: File) => void;
  disabled?: boolean;
  className?: string;
};

function getInitial(value: string | undefined): string {
  const trimmed = (value ?? '').trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : '';
}

/**
 * AvatarUpload — a labelled 72px circle that opens the OS file picker.
 *
 * States:
 * - **dashed empty**: dashed blue ring + `plus` icon (Figma "Add Company Logo").
 * - **initials empty**: peach disc + 30px semibold white initial, with an edit
 *   badge (28px white ring > 24px `--primitive-blue-400` disc > 18px `edit`
 *   icon) overlapping the bottom-right edge (Figma "Add Your Image").
 * - **image**: the chosen picture covers the circle, edit badge shown.
 *
 * The circle is a `<button>` so it is keyboard reachable; the `<input
 * type="file">` is visually hidden and triggered programmatically.
 */
export function AvatarUpload({
  label,
  emptyVariant = 'dashed',
  initials,
  src = null,
  alt = '',
  accept = 'image/*',
  onFileSelected,
  disabled = false,
  className = '',
}: AvatarUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const buttonId = useId();

  const hasImage = Boolean(src);
  const showInitials = !hasImage && emptyVariant === 'initials';
  const showDashed = !hasImage && emptyVariant === 'dashed';
  const showEditBadge = hasImage || showInitials;

  const openPicker = useCallback(() => {
    if (disabled) {
      return;
    }
    inputRef.current?.click();
  }, [disabled]);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        onFileSelected?.(file);
      }
      // Reset so picking the same file twice still fires `onChange`.
      event.target.value = '';
    },
    [onFileSelected],
  );

  const rootClasses = ['spark-avatar-upload', className].filter(Boolean).join(' ');
  const circleClasses = [
    'spark-avatar-upload-circle',
    showDashed ? 'spark-avatar-upload-circle-dashed' : '',
    showInitials ? 'spark-avatar-upload-circle-initials' : '',
    hasImage ? 'spark-avatar-upload-circle-image' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={rootClasses}>
      {label ? <Label label={label} htmlFor={buttonId} /> : null}
      <div className="spark-avatar-upload-circle-wrap">
        <button
          id={buttonId}
          type="button"
          className={circleClasses}
          onClick={openPicker}
          disabled={disabled}
          aria-label={hasImage ? 'Change image' : 'Upload image'}
        >
          {hasImage ? (
            <img className="spark-avatar-upload-image" src={src ?? undefined} alt={alt} />
          ) : showInitials ? (
            <span className="spark-avatar-upload-initial" aria-hidden="true">
              {getInitial(initials)}
            </span>
          ) : (
            <SparkIcon slug="plus" size="medium" className="spark-avatar-upload-plus" />
          )}
        </button>
        {showEditBadge ? (
          <span className="spark-avatar-upload-badge" aria-hidden="true">
            <span className="spark-avatar-upload-badge-inner">
              <SparkIcon slug="edit" size={18} className="spark-avatar-upload-badge-icon" />
            </span>
          </span>
        ) : null}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        className="spark-avatar-upload-hidden-input"
        tabIndex={-1}
        aria-hidden="true"
        disabled={disabled}
      />
    </div>
  );
}
